import { useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { useLocalStorage } from 'usehooks-ts';
import { CODE_GAMEPAD_URL } from '~/config/endpoints.config';
import { CLIENT_THEME_CSS } from '~/config/global.config';
import { useHeadTags, HeadTag } from '~/hooks/useHeadTags';
import { Home } from '~/pages/Home';
import Infos from '~/pages/Auth/infos';
import Success from '~/pages/Auth/success';
import ScreenApp from '~/unboared/ScreenApp';
import GamepadApp from '~/unboared/GamepadApp';
import { GamepadAccess } from '~/pages/GamepadAccess';
import { HomeDemo } from '~/pages/Home/Home';
import { OnboardingBridge, OnboardingLink } from '~/pages/Onboarding';
import { Account, AccountLegacy, CancelFlow } from '~/pages/Account';
import { RewardPage } from '~/pages/Reward/RewardPage';
import { OtherTab } from '~/pages/OtherTab';
import DebugFreePlay from '~/pages/DebugFreePlay/DebugFreePlay';
import {
    AnimatorList,
    AnimatorCockpit,
    RegiePage,
    AnimatorLayout,
    QuizStudioList,
    QuizExpress,
    QuizEditor,
} from '~/pages/Animator';
import { ConsentLayer } from '~/components/consent/ConsentLayer';
import { consumePostLoginRedirect } from './postLoginRedirect';

/**
 * Point d'atterrissage post-login (route `/`). On honore d'abord une destination
 * mémorisée par AuthRoutes (lien profond `/animator`, `/app/<clientID>?game=X`,
 * `/account/cancel`, `/account/keep`, `/account/annual` ouverts hors connexion) pour y revenir après le login, sinon
 * comportement habituel (écran de l'instance si on a un clientID + instanceID
 * connus, sinon /home). Destination consommée une seule fois et revalidée
 * (useState initializer → une redirection nette, sans flash de /home). MainRoutes ne mémorise jamais rien : pas de boucle possible.
 */
function InitialRedirect({ clientID, instanceID }: { clientID: string; instanceID: string }) {
    const [target] = useState(() => {
        const stored = consumePostLoginRedirect();
        if (stored) return stored;
        return clientID && instanceID && clientID !== 'demo' ? `app/${clientID}?device=${instanceID}` : 'home';
    });
    return <Navigate to={target} replace />;
}

export function MainRoutes() {
    const [clientID] = useLocalStorage('clientID', '');
    const [instanceID] = useLocalStorage('instanceID', '');

    // Charte du client sur l'écran TV et la console authentifiée. Passait par
    // <Helmet> jusqu'au 10/09/2026, donc ne partait pas du tout (gotcha #154).
    const themeTags: HeadTag[] = CLIENT_THEME_CSS.includes(clientID)
        ? [{ tag: 'link', attrs: { rel: 'stylesheet', type: 'text/css', href: `/assets/css/${clientID}.css` } }]
        : [];
    useHeadTags(themeTags);

    return (
        <BrowserRouter>
            <Routes>
                <Route path="/home" element={<Home />} />
                <Route path="/welcome" element={<OnboardingBridge />} />
                <Route path="/link" element={<OnboardingLink />} />
                <Route path="/account" element={<Account />} />
                {/* Pre-redesign page kept as a backup for rollback / visual comparison. */}
                <Route path="/account/legacy" element={<AccountLegacy />} />
                {/* « Résilier » → « Plutôt faire une pause ? » (1/2/3 mois) ou portail Stripe. */}
                <Route path="/account/cancel" element={<CancelFlow />} />
                {/* Lien de l'email de fin d'essai : « Garder Unboared » mis en avant (?client=<id>). */}
                <Route path="/account/keep" element={<Account keepFocus />} />
                {/* Lien de l'email d'offre annuelle : encart « 2 mois offerts » mis en avant (?client=<id>). */}
                <Route path="/account/annual" element={<Account annualFocus />} />
                {/* Retour du portail Stripe après « Passer en Pro » (?client=<id>) : carte « Ma marque » amenée à l'écran. */}
                <Route path="/account/brand" element={<Account brandFocus />} />
                <Route path="/" element={<InitialRedirect clientID={clientID} instanceID={instanceID} />} />
                <Route path="app/:clientID" element={<ScreenApp />} />
                {/* Onglet écran mis en veille : le même bar est ouvert dans un autre onglet (screenTabLock). */}
                <Route path="other-tab" element={<OtherTab />} />
                <Route path="demo" element={<HomeDemo />} />
                <Route path="infos" element={<Infos />} />
                <Route path="success" element={<Success />} />
                <Route path={`${CODE_GAMEPAD_URL}`} element={<GamepadAccess />} />
                <Route path={`${CODE_GAMEPAD_URL}/:sessionID`} element={<GamepadApp />} />
                <Route path="reward/:couponId" element={<RewardPage />} />
                <Route path="debug/free-play" element={<DebugFreePlay />} />
                {/* Animator Control : pilotage de l'écran depuis le téléphone de
                    l'animateur. Derrière le login (App.tsx → MainRoutes seulement
                    si auth). MVP : un écran par établissement (clé screenInfos.anim).
                    Le layout porte les balises PWA + service worker pour toute la
                    zone (installable sur l'écran d'accueil du téléphone). */}
                <Route path="animator" element={<AnimatorLayout />}>
                    <Route index element={<AnimatorList />} />
                    <Route path=":clientID" element={<AnimatorCockpit />} />
                    {/* Double écran « Régie » (formule Pro) : la télécommande en grand sur
                        l'ordinateur, l'écran public sur le projecteur. */}
                    <Route path=":clientID/regie" element={<RegiePage />} />
                    {/* Studio Quiz : création/édition des quiz Unquiz (deux portes :
                        express IA + éditeur complet), mobile ET desktop. La création
                        est une tâche à part entière, séparée du pilotage. */}
                    <Route path=":clientID/quizzes" element={<QuizStudioList />} />
                    <Route path=":clientID/quizzes/express" element={<QuizExpress />} />
                    <Route path=":clientID/quizzes/:quizId" element={<QuizEditor />} />
                </Route>
                <Route path="*" element={<Navigate to="/" />} />
            </Routes>
            {/* Bandeau cookies + enregistrement des sessions (PostHog) selon le choix. */}
            <ConsentLayer />
        </BrowserRouter>
    );
}
